/**
 * One id per api call, sent as x-correlation-id.
 *
 * The api adopts the id, writes it to apiLogs and errorLog and puts it on any
 * queue job the request produces, so a Sentry event here and a Cloudflare log
 * row there can be lined up directly rather than matched on time and path.
 *
 * Mirrors shared/ops/correlation.js, which the workers use. Kept as its own
 * copy because the PWA build does not reach outside pwa/.
 */

export const CORRELATION_HEADER = 'x-correlation-id';

/**
 * crypto.randomUUID needs a secure context and only landed in iOS Safari 15.4;
 * resort iPads are not always current, and an id that throws on an old device
 * would take the whole request with it.
 */
export function newCorrelationId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            const bytes = crypto.getRandomValues(new Uint8Array(16));
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
            return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        }
    } catch (error) {
        console.warn('Falling back to a non-crypto correlation id:', error?.message);
    }

    // Not a uuid and not unique under real concurrency, but the api accepts it
    // and a weak id beats no id at all.
    return `fb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
